import { z } from "zod";

// Drop null-valued keys from an object before validation. The backend persists absent fields as
// `null` in Mongo and serialises them as `null` over JSON (e.g. a weekly slot has `bySetPos: null`,
// `count: null`, `until: null`, and an amenity with no recurrence has `recurrence: null`). Our
// fields are `.optional()` (undefined only), so those nulls previously failed validation and the
// whole booking list failed to ingest, white-screening the app (see createIngestor). Stripping null
// → absent lets `.optional()` accept them while keeping the inferred types clean (`T | undefined`,
// never `T | null`), so no call site needs to learn about null.
export const stripNulls = (obj: unknown): unknown => {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    return Object.fromEntries(Object.entries(obj as Record<string, unknown>).filter(([, v]) => v !== null));
  }
  return obj;
};

export const RecurrenceSchema = z.preprocess(
  stripNulls,
  z.object({
    baseBookingId: z.string(),
    frequency: z.enum(["daily", "weekly", "monthly"]),
    interval: z.number().optional(), // e.g., every 1 week, every 2 weeks
    count: z.number().optional(), // total number of occurrences
    until: z.coerce.date().optional(), // ISO date string or Date object
    cancelledFrom: z.coerce.date().optional(), // first date a slot/recurring series no longer occurs
    byWeekday: z.array(z.enum(["MO", "TU", "WE", "TH", "FR", "SA", "SU"])).optional(), // e.g., ["MO", "WE", "FR"]
    bySetPos: z.number().optional(), // nth occurrence of weekday in month (1-5), for monthly recurrence
    exceptions: z.array(z.string()).optional(), // ISO date strings to skip
  }),
);

export type Recurrence = z.infer<typeof RecurrenceSchema>;

export type Frequency = "daily" | "weekly" | "monthly";

export type Weekday = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export type RecurrencePatternOption = {
  label: string;
  frequency: Frequency;
  interval: number;
  occurrencesToCheck?: number; // undefined = indefinite (no end date)
};

export const RECURRENCE_OPTIONS: RecurrencePatternOption[] = [
  { label: "Weekly", frequency: "weekly", interval: 1, occurrencesToCheck: 52 },
  { label: "Fortnightly", frequency: "weekly", interval: 2, occurrencesToCheck: 26 },
  { label: "Monthly", frequency: "monthly", interval: 1, occurrencesToCheck: 12 },
  { label: "Quarterly", frequency: "monthly", interval: 3, occurrencesToCheck: 4 },
];

// API request/response types for recurrence endpoints

export type AddRecurrenceRequest = {
  frequency: "weekly" | "monthly";
  interval: number; // 1=weekly/monthly, 2=fortnightly, 3=quarterly
  count?: number; // Number of occurrences (1-52)
  until?: string; // ISO date string
};

export type AddRecurrenceResponse = {
  success: boolean;
  booking: import("./Booking").Booking;
  message: string;
};

export type RemoveRecurrenceRequest = {
  cancelFutureOccurrences?: boolean; // Default true
  // The LAST occurrence to KEEP (ISO YYYY-MM-DD). The recurrence is bounded to it — occurrences
  // on/before stay, later ones stop. Omitted → the server keeps everything up to today.
  effectiveDate?: string;
};

export type RemoveRecurrenceResponse = {
  success: boolean;
  booking: import("./Booking").Booking;
  message: string;
  cancelledOccurrences?: number;
};

export type CheckRecurrenceAvailabilityRequest = {
  frequency: "weekly" | "monthly";
  interval: number;
  count?: number;
  until?: string;
};

export type RecurrenceConflict = {
  date: string;
  conflictType: "room" | "amenity";
  details: string;
};

export type CheckRecurrenceAvailabilityResponse = {
  totalOccurrences: number;
  availableCount: number;
  conflictingDates: RecurrenceConflict[];
  canProceed: boolean;
};
